import { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Footer from './Footer';
import GhostFloatingIcon from './GhostFloatingIcon';
// v3.15-1232 Ghost Daily Organizer — permanent right-side dock.
import GhostDailyOrganizer from './GhostDailyOrganizer';
// v3.21-1620 · Standard Client Workspace (reusable operating window).
import ClientWorkspaceModal from './ClientWorkspaceModal';
import { ClientWorkspaceProvider } from '../context/ClientWorkspaceContext';
// v3.21-1621 · Internal Team Chat window (client-context employee chat).
import TeamChatWindow from './TeamChatWindow';
import { TeamChatProvider } from '../context/TeamChatContext';
// v3.21-1624 · Unknown Inbound Caller pane (auto-surfaces from any page).
import UnknownCallerModal from './UnknownCallerModal';
// v3.21-1605 · External partners never see Ghost surfaces.
import { isInternalUser } from '../lib/isInternalUser';
// v3.21-1669 · Specific-user Funded surface capability (SSOT mirror).
import { canAccessFunded } from '../lib/canAccessFunded';
import ApplicationAssignmentGate from './ApplicationAssignmentGate';  // v3.18-1336 APP-AUTO-001
import NotificationCenter from './NotificationCenter';
// v3.21-1641 · Global listener that opens the shared ComposeAndApprove
// composer whenever any client-context surface dispatches
// `hub:open-compose`.  Auto-populates the To field with the client's
// authoritative primary email.
import ClientContextComposeListener from './ClientContextComposeListener';
import PersistentNotificationTray from './PersistentNotificationTray';
// v3.15-1230 SMART-INTAKE-003 — payment method intake block.
import ClientPaymentMethodsIntake from './ClientPaymentMethodsIntake';
import wsService from '../services/websocket';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  LayoutDashboard,
  Users,
  Building2,
  FileText,
  ClipboardList,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronUp,
  UserCircle,
  Kanban,
  UsersRound,
  DollarSign,
  Plus,
  AlertTriangle,
  User,
  Loader2,
  MessageSquare,
  Landmark,
  Wallet,
  FolderOpen,
  Circle,
  Bell,
  Settings,
  Mail,
  Calendar,
  TrendingUp,
  Shield,
  Lock,
  Key,
  ExternalLink,
  FilePlus,
  Workflow,
  Eye,
  GripVertical,
  Upload,
  PhoneCall,
  PhoneForwarded,
  Phone,
  Calendar as CalendarIcon,
  LifeBuoy,
  Wrench,
  ShieldAlert,
  BarChart3,
  // v3.15-1213 NAV-001 — Workforce & Compensation section icons.
  Award,
  Trophy,
  Briefcase,
  FileBarChart,
  Coins,
  Receipt,
  Undo2,
  Gift,
  ScrollText,
  Filter,  // v3.15-1216 MM-002 — Financial Lifecycle sidebar entry.
  Zap,     // v3.15-1226 OPS-001 — Priority Queue sidebar entry.
  Sparkles, // v3.21-1717 — My Ghost Coach sidebar entry.
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import api from '../lib/api';
import { toast } from 'sonner';
import useAchNotifications, { getSoundEnabled, setSoundEnabled } from '../hooks/useAchNotifications';
import DialPadModal from './DialPadModal';
import ActiveCallPill from './ActiveCallPill';
import { SectionErrorBoundary } from './SectionErrorBoundary';
import { AppErrorBoundary } from './AppErrorBoundary';   // v3.18-1303
import { RequiredDocsBanner, AnnouncementsBanner } from './CmsBanners';   // v3.21-1600
import { canUsePhoneSystem, canUseIntegratedPBX } from '../lib/phone_access';
import PendingDispositionWatcher from './PendingDispositionWatcher';  // v3.15-1150
import TransferCallModal from './TransferCallModal';
import { onDial } from '../lib/comm_bus';  // v3.18-1296 Contact Directory click-to-call

// Deal stages
const DEAL_STAGES = [
  { value: 'new_lead', label: 'New Lead' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualifying', label: 'Qualifying' },
  { value: 'application_submitted', label: 'Application Submitted' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'offer_selected', label: 'Offer Selected' },
  { value: 'funding_pending', label: 'Funding Pending' },
];

// Lender list for quick selection
const LENDERS = [
  'Best Egg', 'Upgrade', 'LendingClub', 'Happy Money', 'Upstart', 
  'SoFi', 'Prosper', 'Avant', 'Marcus by Goldman Sachs', 'Discover Personal Loans'
];

// Rep availability status options
const REP_STATUS_OPTIONS = [
  { value: 'available', color: 'bg-green-500', label: 'Available' },
  { value: 'paused', color: 'bg-yellow-500', label: 'Paused' },
  { value: 'offline', color: 'bg-slate-400', label: 'Offline' },
];

// v3.14-194: Sortable sidebar nav item
const SortableNavItem = ({ item, onClose, onClearBadge }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.to });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };
  return (
    <div ref={setNodeRef} style={style} className="flex items-center group">
      <button
        {...attributes}
        {...listeners}
        className="opacity-0 group-hover:opacity-60 cursor-grab active:cursor-grabbing p-1 -ml-2 mr-0"
        aria-label={`Reorder ${item.label}`}
      >
        <GripVertical className="h-3 w-3 text-white/40" />
      </button>
      <NavLink
        to={item.to}
        className={({ isActive }) =>
          `flex-1 flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 ${
            isActive
              ? 'bg-accent text-primary font-medium shadow-glow'
              : 'text-white/70 hover:text-white hover:bg-primary-light/20'
          } ${item.flash ? 'animate-pulse ring-2 ring-green-400 ring-offset-2 ring-offset-primary' : ''}`
        }
        onClick={() => { onClose(); onClearBadge(); }}
      >
        <item.icon className="h-5 w-5" />
        <span className="flex-1">{item.label}</span>
        {item.badge > 0 && (
          <span className={`flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold rounded-full ${
            item.flash ? 'bg-green-500 text-white animate-bounce' : 'bg-accent text-primary'
          }`}>
            {item.badge > 99 ? '99+' : item.badge}
          </span>
        )}
      </NavLink>
    </div>
  );
};

const DashboardLayout = () => {
  const { user, realUser, logout, isAdmin, isRealAdmin, isInternal, isPtcUser, canManageUsers, canAccessLending, isMarketingPartner, isViewingAsUser, setViewAsUser, exitViewAs, mustChangePassword, clearMustChangePassword, canAccessWorkspace, canAccessDashboard, canViewMainDashboard, isCompanyManager, isVoOfficer } = useAuth();
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwChanging, setPwChanging] = useState(false);
  const [pwError, setPwError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  
  // ACH real-time notifications (PTC Admin only)
  useAchNotifications(isInternal());
  const [achSoundOn, setAchSoundOn] = useState(() => getSoundEnabled());
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [quickAddClientOpen, setQuickAddClientOpen] = useState(false);
  const [dialPadOpen, setDialPadOpen] = useState(false);
  const [dialPadInitial, setDialPadInitial] = useState('');  // v3.18-1296
  // v3.15-1131: Global call transfer modal — opens from top nav OR active-call pill
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [topNavActiveCall, setTopNavActiveCall] = useState(null);
  // v3.21-1735 · SMS Dashboard badge — poll actionable count from
  // /api/sms-dashboard/badge every 30s.  Server-side visibility is
  // scoped by Deal Ownership, so unauthorized rows never contribute.
  const [smsBadgeTotal, setSmsBadgeTotal] = useState(0);

  // v3.21-1735 · SMS badge poll (30s cadence + on-mount).  Server
  // enforces Deal-Ownership visibility, so this is safe to run for
  // every user without additional client-side gating.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get('/sms-dashboard/badge');
        if (!cancelled) setSmsBadgeTotal(Number(data?.total || 0));
      } catch { /* silent — badge is best-effort */ }
    };
    load();
    const h = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(h); };
  }, []);

  const [quickAddLoading, setQuickAddLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [ptcReps, setPtcReps] = useState([]);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  
  // Rep Status State
  const [repStatus, setRepStatus] = useState('offline');
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  // v3.15-682: Dynamic EQF rep extension for external users
  const [eqfRepExt, setEqfRepExt] = useState(null);
  
  // Digest Settings State
  const [digestEnabled, setDigestEnabled] = useState(false);
  const [digestLoading, setDigestLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // v3.14-172: View As User state
  const [viewAsOpen, setViewAsOpen] = useState(false);
  const [viewAsUsers, setViewAsUsers] = useState([]);
  const [viewAsLoading, setViewAsLoading] = useState(false);

  // v3.18-1296 — Global click-to-call bus subscription. Any component
  // in the Hub can now `dispatchDial({ number })` and the integrated
  // DialPad opens preseeded, gated by the phone-access ACL.
  useEffect(() => {
    return onDial(({ number }) => {
      if (!canUseIntegratedPBX(realUser || user)) {
        toast.info('Phone system access not enabled for your company');
        return;
      }
      setDialPadInitial(number || '');
      setDialPadOpen(true);
    });
  }, [realUser?.id, user?.id]);

  const fetchViewAsUsers = async () => {
    if (viewAsUsers.length > 0) return;
    setViewAsLoading(true);
    try {
      const res = await api.get('/users');
      setViewAsUsers((res.data || []).filter(u => u.id !== realUser?.id));
    } catch (e) {
      console.error('Failed to fetch users for View As:', e);
    } finally {
      setViewAsLoading(false);
    }
  };
  
  // Change Password State
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  
  // Version State
  const [appVersion, setAppVersion] = useState('v3.14-234');
  
  // Notification audio ref
  const notificationSoundRef = useRef(null);
  
  // Unread communications count for tab badge
  const [unreadCommunications, setUnreadCommunications] = useState(0);
  const [communicationsFlashing, setCommunicationsFlashing] = useState(false);
  // v3.21-1667 · Funded sidebar per-user unseen alert.
  const [unseenFundedCount, setUnseenFundedCount] = useState(0);
  const [fundedFlashing, setFundedFlashing]       = useState(false);
  const lastUnseenIdsRef = useRef('');
  
  const [quickAddData, setQuickAddData] = useState({
    // Client Information
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    company_id: '',
    // Credit Application Details
    date_of_birth: '',
    ssn: '',
    employer_name: '',
    job_title: '',
    monthly_income: '',
    employment_length: '',
    residence_type: '',
    bank_name: '',
    account_type: '',
    loan_amount_requested: '',
    loan_purpose: '',
    originating_rep: '',
    originating_rep_email: '',
    // Deal Information
    sales_rep_name: '',
    ptc_rep_id: '',
    deal_amount: '',
    approved_amount: '',
    lending_institution: '',
    deal_stage: 'new_lead',
    ach_eta: '',
    notes: '',
  });

  // v3.15-1230 SMART-INTAKE-003 — Workflow Mode + payment method arrays.
  // Smart Client Intake now captures Credit Card / ACH details up-front
  // and persists them as first-class child records on the new client.
  const [intakeMode, setIntakeMode] = useState('standard');
  const [intakeCreditCards, setIntakeCreditCards] = useState([]);
  const [intakeAchAccounts, setIntakeAchAccounts] = useState([]);

  // Fetch companies and PTC reps for dropdowns
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [companiesRes, ptcRepsRes] = await Promise.all([
          api.get('/companies/external'),
          isInternal() ? api.get('/users/ptc-reps') : Promise.resolve({ data: [] }),
        ]);
        setCompanies(companiesRes.data || []);
        setPtcReps(ptcRepsRes.data || []);
        
        // Set default company if external user
        if (!isInternal() && user?.company_id) {
          setQuickAddData(prev => ({ ...prev, company_id: user.company_id }));
        }
      } catch (error) {
        console.error('Failed to fetch companies/reps:', error);
      }
    };
    
    if (quickAddClientOpen) {
      fetchData();
    }
  }, [quickAddClientOpen, isInternal, user?.company_id]);

  // Fetch app version on mount
  useEffect(() => {
    const fetchVersion = async () => {
      try {
        // Try API first
        const apiRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/version`);
        if (apiRes.ok) {
          const data = await apiRes.json();
          setAppVersion(data.version);
          return;
        }
        // Fallback to version.json
        const res = await fetch('/version.json');
        const data = await res.json();
        const versionStr = `v${data.major}.${data.minor}-${String(data.patch).padStart(2, '0')}`;
        setAppVersion(versionStr);
      } catch (error) {
        // Use default version if fetch fails
        setAppVersion('v3.14-69');
      }
    };
    fetchVersion();
  }, []);

  // Fetch current rep status on mount (PTC users only)
  useEffect(() => {
    const fetchRepStatus = async () => {
      if (!isPtcUser()) return;
      try {
        const res = await api.get('/users/me/rep-status');
        setRepStatus(res.data.status || 'offline');
      } catch (error) {
        console.error('Failed to fetch rep status:', error);
      }
    };
    fetchRepStatus();
  }, [isPtcUser]);

  // v3.15-683: Fetch EQF rep extension for external users (dynamic per client page)
  useEffect(() => {
    if (isPtcUser()) { setEqfRepExt(null); return; }
    const match = location.pathname.match(/\/clients\/([a-f0-9-]+)/);
    if (!match) { setEqfRepExt(null); return; }
    const fetchExt = async () => {
      try {
        const res = await api.get(`/ghost-ops/eqf-extension/${match[1]}`);
        setEqfRepExt(res.data?.extension || null);
      } catch { setEqfRepExt(null); }
    };
    fetchExt();
  }, [location.pathname, isPtcUser]);


  // Fetch digest settings on mount (for PTC managers)
  useEffect(() => {
    const fetchDigestSettings = async () => {
      if (!isPtcUser()) return;
      const managerRoles = ['ptc_admin', 'ptc_manager', 'ptc_director'];
      if (!managerRoles.includes(user?.role)) return;
      
      try {
        const res = await api.get('/users/me/digest-settings');
        setDigestEnabled(res.data.daily_digest_enabled || false);
      } catch (error) {
        console.error('Failed to fetch digest settings:', error);
      }
    };
    fetchDigestSettings();
  }, [isPtcUser, user?.role]);

  // Toggle digest setting
  const toggleDigest = async () => {
    setDigestLoading(true);
    try {
      const newValue = !digestEnabled;
      await api.patch('/users/me/digest-settings', { daily_digest_enabled: newValue });
      setDigestEnabled(newValue);
      toast.success(newValue ? 'Daily digest enabled' : 'Daily digest disabled');
    } catch (error) {
      console.error('Failed to update digest settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setDigestLoading(false);
    }
  };

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    try {
      // Create and play a simple notification sound using Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 880; // A5 note
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (e) {
      console.error('Failed to play notification sound:', e);
    }
  }, []);

  // Subscribe to WebSocket for new application assignments (PTC users only)
  useEffect(() => {
    if (!isPtcUser()) return;
    
    const unsubNewAssignment = wsService.subscribe('new_application_assigned', (data) => {
      console.log('New application assigned:', data);
      // Play sound
      playNotificationSound();
      // Show toast notification
      toast.info(
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-full bg-blue-100">
            <Bell className="h-4 w-4 text-blue-600" />
          </div>
          <div>
            <p className="font-semibold text-slate-800">New Application Assigned!</p>
            <p className="text-sm text-slate-600">
              {data.client_name} - ${(data.loan_amount || 0).toLocaleString()}
            </p>
            <p className="text-xs text-slate-500 mt-1">You have 3 minutes to claim this application.</p>
          </div>
        </div>,
        {
          duration: 10000,
          action: {
            label: 'View',
            onClick: () => navigate('/ptc-deals'),
          },
        }
      );
    });

    const unsubMissedApp = wsService.subscribe('application_missed', (data) => {
      console.log('Application missed:', data);
      // Only notify PTC managers
      if (['ptc_admin', 'ptc_manager', 'ptc_director'].includes(user?.role)) {
        toast.warning(
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full bg-amber-100">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
            </div>
            <div>
              <p className="font-semibold text-slate-800">Application Missed</p>
              <p className="text-sm text-slate-600">
                {data.rep_name} missed an application
              </p>
            </div>
          </div>,
          { duration: 8000 }
        );
      }
    });

    return () => {
      unsubNewAssignment();
      unsubMissedApp();
    };
  }, [isPtcUser, navigate, playNotificationSound, user?.role]);

  // Fetch unread communications count and subscribe to updates
  useEffect(() => {
    let mounted = true;
    
    const fetchUnreadCount = async () => {
      try {
        const res = await api.get('/communications/unread-count');
        if (mounted) {
          const count = res.data?.count || 0;
          if (count > unreadCommunications && unreadCommunications > 0) {
            // New activity arrived - trigger flash
            setCommunicationsFlashing(true);
            playNotificationSound();
            setTimeout(() => {
              if (mounted) setCommunicationsFlashing(false);
            }, 5000);
          }
          setUnreadCommunications(count);
        }
      } catch (error) {
        // Silently fail - endpoint may not exist
        console.debug('Unread count fetch skipped:', error.message);
      }
    };
    
    // Initial fetch
    fetchUnreadCount();
    
    // Poll every 30 seconds for updates
    const interval = setInterval(fetchUnreadCount, 30000);
    
    // Subscribe to WebSocket for real-time communications updates
    const unsubComms = wsService.subscribe('new_communication', (data) => {
      if (mounted) {
        setUnreadCommunications(prev => prev + 1);
        setCommunicationsFlashing(true);
        playNotificationSound();
        // v3.21-1623 · Auto-open the STANDARD Client Workspace on
        // inbound calls.  Known client → workspace opens with the
        // client + call SID attached.
        // v3.21-1624 · Unknown caller → surface the Unknown Caller
        // pane automatically (never creates a fake client).
        const notifType = (data?.type || data?.notification_type || '').toLowerCase();
        if (notifType === 'incoming_call') {
          const callSid = data?.twilio_sid || data?.call_sid || null;
          if (data?.client_id) {
            try {
              window.dispatchEvent(new CustomEvent('client-workspace:open', {
                detail: { clientId: data.client_id, callSid },
              }));
            } catch (_e) { /* noop */ }
          } else if (callSid) {
            try {
              window.dispatchEvent(new CustomEvent('unknown-caller:open', {
                detail: { callSid, phone: data?.phone_number || data?.from || 'Unknown' },
              }));
            } catch (_e) { /* noop */ }
          }
        }
        toast.info(
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full bg-green-100">
              <MessageSquare className="h-4 w-4 text-green-600" />
            </div>
            <div>
              <p className="font-semibold text-slate-800">New Activity</p>
              <p className="text-sm text-slate-600">
                {data.message || 'New communication received'}
              </p>
            </div>
          </div>,
          {
            duration: 5000,
            action: {
              label: 'View',
              onClick: () => navigate('/communications'),
            },
          }
        );
        setTimeout(() => {
          if (mounted) setCommunicationsFlashing(false);
        }, 5000);
      }
    });
    
    // v3.21-1667 · Poll Funded unseen identifiers.  Fires the pulse
    // only when a genuinely new identifier appears since last poll.
    const fetchFundedUnseen = async () => {
      try {
        const res = await api.get('/finturf-funded/unseen');
        if (!mounted) return;
        const ids = (res.data?.unseen_identifiers || []).sort().join('|');
        const count = res.data?.unseen_count || 0;
        if (count > 0 && ids !== lastUnseenIdsRef.current) {
          setFundedFlashing(true);
          setTimeout(() => { if (mounted) setFundedFlashing(false); }, 6000);
        }
        lastUnseenIdsRef.current = ids;
        setUnseenFundedCount(count);
      } catch (_) { /* endpoint may 403 for non-internal — ignore */ }
    };
    fetchFundedUnseen();
    const fundedInterval = setInterval(fetchFundedUnseen, 45000);

    return () => {
      mounted = false;
      clearInterval(interval);
      clearInterval(fundedInterval);
      unsubComms();
    };
  }, [navigate, playNotificationSound, unreadCommunications]);

  // Update rep status
  const updateRepStatus = async (newStatus) => {
    setStatusLoading(true);
    try {
      await api.patch('/users/me/rep-status', { status: newStatus });
      setRepStatus(newStatus);
      toast.success(`Status updated to ${newStatus}`);
    } catch (error) {
      console.error('Failed to update rep status:', error);
      toast.error('Failed to update status');
    } finally {
      setStatusLoading(false);
      setStatusDropdownOpen(false);
    }
  };

  // Change password handler
  const handleChangePassword = async () => {
    setPasswordError('');
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New password and confirmation do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }
    
    setPasswordLoading(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
        confirm_password: passwordData.confirmPassword,
      });
      toast.success('Password changed successfully');
      setChangePasswordOpen(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to change password';
      setPasswordError(message);
    } finally {
      setPasswordLoading(false);
    }
  };

  // Check for duplicate clients
  const checkDuplicate = useCallback(async () => {
    if (!quickAddData.email && !quickAddData.phone) {
      setDuplicateWarning(null);
      return;
    }
    
    setCheckingDuplicate(true);
    try {
      const params = new URLSearchParams();
      if (quickAddData.email) params.append('email', quickAddData.email);
      if (quickAddData.phone) params.append('phone', quickAddData.phone);
      
      const res = await api.get(`/clients/check-duplicate?${params.toString()}`);
      if (res.data.found) {
        setDuplicateWarning(res.data.duplicates);
      } else {
        setDuplicateWarning(null);
      }
    } catch (error) {
      console.error('Duplicate check failed:', error);
    } finally {
      setCheckingDuplicate(false);
    }
  }, [quickAddData.email, quickAddData.phone]);

  // Debounced duplicate check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (quickAddData.email || quickAddData.phone) {
        checkDuplicate();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [quickAddData.email, quickAddData.phone, checkDuplicate]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleUseExistingClient = (existingClient) => {
    // Navigate to existing client detail page
    setQuickAddClientOpen(false);
    navigate(`/clients/${existingClient.id}`);
    toast.info(`Navigating to existing client: ${existingClient.full_name}`);
  };

  const handleQuickAddClient = async (e) => {
    e.preventDefault();
    setQuickAddLoading(true);
    
    try {
      // Validate required fields
      if (!quickAddData.first_name || !quickAddData.last_name) {
        toast.error('First name and last name are required');
        setQuickAddLoading(false);
        return;
      }
      
      if (!quickAddData.email) {
        toast.error('Email is required');
        setQuickAddLoading(false);
        return;
      }
      
      // Determine company_id
      let companyId = quickAddData.company_id;
      if (!companyId && !isInternal()) {
        companyId = user?.company_id;
      }
      
      if (!companyId) {
        toast.error('Please select a partner company');
        setQuickAddLoading(false);
        return;
      }
      
      const payload = {
        full_name: `${quickAddData.first_name} ${quickAddData.last_name}`.trim(),
        email: quickAddData.email,
        phone: quickAddData.phone || null,
        street_address: quickAddData.street_address || null,
        city: quickAddData.city || null,
        state: quickAddData.state || null,
        zip_code: quickAddData.zip_code || null,
        company_id: companyId,
        // Credit Application Details
        date_of_birth: quickAddData.date_of_birth || null,
        ssn: quickAddData.ssn || null,
        employer_name: quickAddData.employer_name || null,
        job_title: quickAddData.job_title || null,
        monthly_income: quickAddData.monthly_income ? parseFloat(quickAddData.monthly_income) : null,
        employment_length: quickAddData.employment_length || null,
        residence_type: quickAddData.residence_type || null,
        bank_name: quickAddData.bank_name || null,
        account_type: quickAddData.account_type || null,
        loan_amount_requested: quickAddData.loan_amount_requested ? parseFloat(quickAddData.loan_amount_requested) : null,
        loan_purpose: quickAddData.loan_purpose || null,
        source_rep_name: quickAddData.originating_rep || null,
        source_rep_email: quickAddData.originating_rep_email || null,
        // Deal Information
        sales_rep_name: quickAddData.sales_rep_name || null,
        ptc_rep_id: quickAddData.ptc_rep_id || null,
        deal_amount: quickAddData.deal_amount ? parseFloat(quickAddData.deal_amount) : null,
        approved_amount: quickAddData.approved_amount ? parseFloat(quickAddData.approved_amount) : null,
        lending_institution: quickAddData.lending_institution || null,
        deal_stage: quickAddData.deal_stage,
        ach_eta: quickAddData.ach_eta || null,
        notes: quickAddData.notes || null,
      };
      
      const res = await api.post('/clients', payload);
      const _newClientId = res?.data?.id;

      // v3.15-1230 SMART-INTAKE-003 — Attach payment methods based on
      // the selected workflow mode. Client is created identically in
      // all three modes; CC / ACH are additive child records that land
      // in the client_credit_cards / client_ach_accounts collections
      // introduced by SMART-INTAKE-002.
      const _paymentFailures = [];
      if (_newClientId && intakeMode === 'credit_card' && intakeCreditCards.length > 0) {
        for (const c of intakeCreditCards) {
          try {
            await api.post(`/clients/${_newClientId}/credit-cards`, {
              payment_nickname: c.payment_nickname || null,
              bank_name: c.bank_name || null,
              cardholder_name: c.cardholder_name || null,
              card_number: c.card_number || null,
              card_brand: c.card_brand || null,
              expiration_month: c.expiration_month || null,
              expiration_year: c.expiration_year || null,
              cvv: c.cvv || null,
              billing_zip: c.billing_zip || null,
              credit_limit: c.credit_limit !== '' && c.credit_limit != null ? parseFloat(c.credit_limit) : null,
              current_balance: c.current_balance !== '' && c.current_balance != null ? parseFloat(c.current_balance) : null,
              minimum_payment: c.minimum_payment !== '' && c.minimum_payment != null ? parseFloat(c.minimum_payment) : null,
              interest_rate: c.interest_rate !== '' && c.interest_rate != null ? parseFloat(c.interest_rate) : null,
              is_default: !!c.is_default,
              notes: c.notes || null,
            });
          } catch (_ccErr) {
            _paymentFailures.push(`Credit card "${c.payment_nickname || c.bank_name || 'card'}"`);
          }
        }
      }
      if (_newClientId && intakeMode === 'ach' && intakeAchAccounts.length > 0) {
        for (const a of intakeAchAccounts) {
          try {
            await api.post(`/clients/${_newClientId}/ach-accounts`, {
              payment_nickname: a.payment_nickname || null,
              account_holder_name: a.account_holder_name || null,
              bank_name: a.bank_name || null,
              routing_number: a.routing_number || null,
              account_number: a.account_number || null,
              account_type: a.account_type || null,
              authorization_given: !!a.authorization_given,
              is_default: !!a.is_default,
              notes: a.notes || null,
            });
          } catch (_achErr) {
            _paymentFailures.push(`ACH "${a.payment_nickname || a.bank_name || 'account'}"`);
          }
        }
      }

      if (_paymentFailures.length > 0) {
        toast.warning(`Client & Deal created, but failed to attach: ${_paymentFailures.join(', ')}`);
      } else {
        toast.success('Client & Deal created successfully!');
      }
      
      // Reset form
      setQuickAddClientOpen(false);
      setDuplicateWarning(null);
      setQuickAddData({
        first_name: '',
        last_name: '',
        phone: '',
        email: '',
        street_address: '',
        city: '',
        state: '',
        zip_code: '',
        company_id: isInternal() ? '' : user?.company_id || '',
        // Credit Application Details
        date_of_birth: '',
        ssn: '',
        employer_name: '',
        job_title: '',
        monthly_income: '',
        employment_length: '',
        residence_type: '',
        bank_name: '',
        account_type: '',
        loan_amount_requested: '',
        loan_purpose: '',
        originating_rep: '',
        originating_rep_email: '',
        // Deal Information
        sales_rep_name: '',
        ptc_rep_id: '',
        deal_amount: '',
        approved_amount: '',
        lending_institution: '',
        deal_stage: 'new_lead',
        ach_eta: '',
        notes: '',
      });
      // v3.15-1230 SMART-INTAKE-003 — reset intake mode + payment collections.
      setIntakeMode('standard');
      setIntakeCreditCards([]);
      setIntakeAchAccounts([]);
      
      // Navigate to the new client's detail page
      navigate(`/clients/${res.data.id}`);
    } catch (error) {
      let message = 'Failed to create client';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          message = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          message = error.response.data.detail.map(e => e.msg || e.message).join(', ');
        }
      }
      toast.error(message);
    } finally {
      setQuickAddLoading(false);
    }
  };

  // Check if user can see Settlement Report (PTC Admin or Company Director)
  const canSeeSettlementReport = () => {
    if (!user) return false;
    if (user.role === 'ptc_admin') return true;
    if (user.role === 'company_director') return true;
    return false;
  };

  // Check if user can access PTC Deal Control Panel
  // Available to: PTC internal users OR users from Prestigious Travel Club
  // NOT available to: sales reps (company_team_member) unless granted permission
  const canAccessPtcDeals = () => {
    if (!user) return false;
    if (isInternal()) return true;
    // Sales reps (company_team_member) cannot access pipeline by default
    if (user.role === 'company_team_member') {
      return user.permissions?.can_access_pipeline === true;
    }
    // Check if user's company name contains "Prestigious Travel Club"
    if (user.company_name && user.company_name.toLowerCase().includes('prestigious travel club')) return true;
    return false;
  };

  // Check if user can access Pipeline (same restrictions as PTC Deals for sales reps)
  const canAccessPipeline = () => {
    if (!user) return false;
    if (isInternal()) return true;
    // Sales reps cannot access pipeline by default
    if (user.role === 'company_team_member') {
      return user.permissions?.can_access_pipeline === true;
    }
    return true;
  };

  // Check if user is a sales rep (external team member)
  const isSalesRep = () => {
    return user?.role === 'company_team_member';
  };

  // v3.18-1254 PAYMENT-001 — Payment Center access rule.
  // Mirrors the backend's routes/payment_center._is_authorized:
  //   • Role in { ptc_admin, ptc_director, ptc_manager,
  //               company_director, company_manager }
  //   • PTC admin roles bypass the company check.
  //   • Other roles must belong to an approved company family
  //     (PTC / Prestigious Travel Club / Freedom Solutions Group).
  // v3.18-1302 EMAIL-ARCH-003 — Template Library restricted to
  // Prestigious Companies, Prestigious Travel Club, and EasyQuickFund.
  // v3.19-1406 CRM-SEC-002 Sidebar Lockdown — TIGHTENED further to
  // ADMIN ONLY.  Backend also enforces HTTP 403 at the router level.
  const canAccessTemplateLibrary = () => {
    if (!user) return false;
    return isAdmin();  // v3.19-1406 admin-only
  };

  const canAccessPaymentCenter = () => {
    if (!user) return false;
    const role = (user.role || '').toLowerCase();
    const allowedRoles = new Set([
      'ptc_admin',
      'ptc_director',
      'ptc_manager',
      'company_director',
      'company_manager',
    ]);
    if (!allowedRoles.has(role)) return false;
    if (role === 'ptc_admin' || role === 'ptc_director' || role === 'ptc_manager') return true;
    const co = (user.company_name || '').trim().toLowerCase();
    const allowedCompanies = new Set([
      'ptc',
      'ptc inc',
      'ptc, inc',
      'ptc, inc.',
      'prestigious travel club',
      'prestigious travel club inc',
      'prestigious travel club, inc',
      'prestigious travel club, inc.',
      'freedom solutions group',
      'freedom solutions group inc',
      'freedom solutions group, inc',
      'freedom solutions group, inc.',
    ]);
    return allowedCompanies.has(co);
  };

  // Check if user can access Calendar
  // Only PTC internal users and Prestigious Travel Club Inc
  const canAccessCalendar = () => {
    if (!user) return false;
    if (isInternal()) return true;
    // Only Prestigious Travel Club Inc can access calendar
    if (user.company_name && user.company_name.toLowerCase().includes('prestigious travel club')) return true;
    return false;
  };

  // v3.18-1274 HUB-ARCH-004 Slice A — grouped-sidebar expanded state.
  // Replaces the previous ad-hoc `adminToolsExpanded` + `workforceExpanded`
  // flags. Each navGroup key persists its expanded/collapsed state per user
  // in localStorage. Default = expanded (undefined-or-true is expanded).
  const navExpandedKey = `ptc-nav-expanded-${user?.id || user?.user_id || 'anon'}`;
  const [navExpanded, setNavExpanded] = useState(() => {
    try {
      const saved = localStorage.getItem(navExpandedKey);
      if (saved) return JSON.parse(saved);
    } catch { /* fall through */ }
    return {};
  });
  const isGroupExpanded = (key) => navExpanded[key] !== false;
  const toggleGroup = (key) => {
    setNavExpanded(prev => {
      const next = { ...prev, [key]: !(prev[key] !== false) };
      try { localStorage.setItem(navExpandedKey, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  // v3.15-1213 NAV-001 — company feature flags (from ADMIN-008 settings).
  // Defaults to all-enabled on load/failure to preserve backward compatibility
  // — nav items never disappear because of a network hiccup.
  const [companyFlags, setCompanyFlags] = useState({
    compensation:  true,
    payroll:       true,
    documents:     true,
    recognition:   true,
    leaderboards:  true,
    badges:        true,
    career:        true,
    money_manager: true,
  });
  useEffect(() => {
    const cid = user?.company_id;
    if (!cid) return;
    let cancelled = false;
    api.get(`/company-settings/${cid}/feature-flags`)
      .then((r) => {
        if (cancelled) return;
        const flags = r?.data?.flags;
        if (flags && typeof flags === 'object') {
          setCompanyFlags((prev) => ({ ...prev, ...flags }));
        }
      })
      .catch(() => { /* silent — keep defaults */ });
    return () => { cancelled = true; };
  }, [user?.company_id]);

  // v3.15-1002: External users + VO officers are FULLY locked out of the
  // internal workspace surface (Dashboard / Calendar / Availability). They
  // operate in a Clients-first worldview with Ghost as their primary
  // assistant. We compute this once for nav-list construction below.
  const inWorkspace = canAccessWorkspace();

  // v3.21-1670 · Corporate-email Funded capability.  Mirrors backend
  // `services.funded_capability.can_access_funded`.  Uses `user` /
  // `effectiveUser` so "View As" impersonation is honoured.  The
  // backend remains the authoritative source — every
  // /api/finturf-funded/* endpoint calls the same SSOT.
  const canAccessFundedSurface = () => canAccessFunded(user);

  // v3.18-1274 HUB-ARCH-004 Slice A — Grouped sidebar navigation.
  //
  // Every leaf gates on the SAME permission check that previously guarded
  // its standalone entry. No route is removed. No functionality is lost.
  // Marketing partners keep their restricted three-item nav. VO officers
  // now get a minimal Verification-only nav (previously they saw every
  // internal item and hit 403s on click). All other roles see grouped
  // sections defined per HUB-ARCH-003 §6.1.
  //
  // The `navGroups` variable is the single source of truth for the sidebar
  // structure; the render loop below iterates it. Groups with `label: null`
  // render as a flat sub-list (no collapsible header) — used for marketing
  // partners' three items and the Support tail entry.
  // v3.18-1274 HUB-ARCH-004 Slice A — Grouped sidebar (single source of truth).
  //
  // Every leaf gates on the SAME permission check that previously guarded
  // its standalone entry. Marketing partners keep their restricted 3-item
  // nav. VO officers now get a minimal Verification-only nav (previously
  // they saw every internal item and hit 403s on click). All other roles
  // see grouped sections per HUB-ARCH-003 §6.1. No route removed, no
  // functionality lost — purely a reorganization of sidebar chrome.
  const navGroups = isMarketingPartner() ? [
    { key: 'main', label: null, items: [
      { to: '/clients',       icon: Users,        label: 'Clients' },
      { to: '/communications',icon: MessageSquare,label: 'Communication',
        badge: unreadCommunications, flash: communicationsFlashing },
      { to: '/reports',       icon: FileText,     label: 'Reports' },
    ]},
  ] : isVoOfficer() ? [
    // v3.21-1636 · Block 2 + Block 3 — VO Officers are internal
    // compliance reviewers.  They need Partner Resource Center for
    // compliance oversight AND Marketplace access alongside their
    // core Verification workflow.  No admin escalation — Marketplace
    // admin endpoints and Partner Resource Center admin CRUD remain
    // gated at the backend.
    { key: 'verification', label: 'Verification', icon: Shield, items: [
      { to: '/clients', icon: Users, label: 'Clients' },
    ]},
    { key: 'partner-resource', label: 'Partner Resource Center', icon: Building2, items: [
      { to: '/partner-resource-center', icon: Building2, label: 'Partner Resource Center' },
    ]},
    { key: 'growth', label: 'Growth', icon: TrendingUp, items: [
      { to: '/marketplace', icon: DollarSign, label: 'Marketplace' },
    ]},
    { key: 'support-tail', label: null, items: [
      { to: '/support', icon: LifeBuoy, label: 'Support' },
    ]},
  ] : [
    { key: 'workspace', label: 'Workspace', icon: LayoutDashboard, items: [
      ...(canViewMainDashboard() ? [{ to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' }] : []),
      // v3.20-1500 PARTNER-NAV — external partners land on Clients,
      // never on Pipeline (which duplicated Clients for them).  The
      // Pipeline route still exists but the sidebar entry is hidden
      // and `/pipeline` redirects to `/` (already in App.js).  Internal
      // roles keep the pipeline shortcut.
      ...(canAccessFundedSurface() ? [{ to: '/funded',  icon: Landmark, label: 'Funded',
                              badge: unseenFundedCount, flash: fundedFlashing,
                              notifKey: 'funded' }] : []),
      // v3.21-1680 · Funding Health replaces Growth → Manager Dashboard.
      // Internal-only.  Backend also enforces 403 for external partners.
      ...(!isMarketingPartner() && isInternal() ? [{ to: '/funding-health', icon: BarChart3, label: 'Funding Health' }] : []),
      { to: '/tasks', icon: ClipboardList,  label: 'Tasks' },
      ...(inWorkspace ? [{ to: '/calendar', icon: CalendarIcon, label: 'Calendar' }] : []),
    ]},
    { key: 'people', label: 'People', icon: Users, items: [
      { to: '/clients', icon: Users, label: 'Clients' },
      // v3.21-1707 — Smart Dialer directly below Clients for regular internal
      // team members (non phone_system_admin).  Phone System admins keep
      // their dedicated /phone-system entry under Communications.
      ...(isInternal() && !['ptc_admin','ptc_director'].includes(user?.role)
          ? [{ to: '/smart-dialer', icon: PhoneCall, label: 'Smart Dialer' }] : []),
      ...(isAdmin() ? [{ to: '/companies', icon: Building2, label: 'Companies' }] : []),
      // v3.20-1500 PARTNER-NAV — HR renamed to "Employee Management".
      ...(canManageUsers() ? [{ to: '/hr', icon: UsersRound, label: 'Employee Management' }] : []),
      // v3.21-1815 — Ambiguous Intakes lives with lead/client
      // management under PEOPLE (moved from Communications).  The
      // ownership resolution flow starts here, so the entry belongs
      // next to Clients / Companies / Employee Management, not next
      // to Inbox / Google Mailboxes.
      ...(isAdmin() ? [{ to: '/admin/ambiguous-intakes', icon: FileText, label: 'Ambiguous Intakes' }] : []),
    ]},
    { key: 'growth', label: 'Growth', icon: TrendingUp, items: [
      ...(canAccessPtcDeals() ? [{ to: '/ptc-deals', icon: FileText, label: 'Deals' }] : []),
      // v3.21-1717 · My Ghost Coach — private AI performance coach.
      // Every internal user gets a personal Ghost Coach; management
      // additionally sees the Team Overview inside the same page.
      ...(isInternal() ? [{ to: '/growth/my-ghost-coach', icon: Sparkles, label: 'My Ghost Coach' }] : []),
      // v3.21-1718 · Coach Standards Editor (management only).
      ...(isAdmin() ? [{ to: '/growth/my-ghost-coach/standards', icon: Sparkles, label: 'Coach Standards' }] : []),
      // v3.19-1406 CRM-SEC-002 Sidebar Lockdown — Template Library,
      // Lead Warehouse, and Business Funding are now ADMIN ONLY.  Hide
      // the sidebar item for every non-admin role.  Backend enforces
      // HTTP 403 on the routes independently.
      ...(isAdmin() ? [{ to: '/business-funding',  icon: Building2,  label: 'Business Funding' }] : []),
      ...(isAdmin() ? [{ to: '/lead-warehouse',    icon: UsersRound, label: 'Lead Warehouse' }] : []),
      // v3.21-1680 · Manager Dashboard removed from Growth.  Its
      // operational reporting migrated to Workspace → Funding Health.
      // Legacy /manager-dashboard route redirects internally.
      // v3.18-1306 — Lending Marketplace hidden until production-ready
      // (feature flag REACT_APP_SHOW_LENDING).  Route remains active.
      ...(isInternal() && process.env.REACT_APP_SHOW_LENDING === 'true'
          ? [{ to: '/lending', icon: Landmark, label: 'Lending' }] : []),
      // v3.21-1611 · Marketplace — services / orders / My Orders /
      // (internal) Orders + Services live inside one page.  Single
      // sidebar entry, no COMMERCE group.
      { to: '/marketplace', icon: DollarSign, label: 'Marketplace' },
    ]},
    // v3.20-1500 PARTNER-NAV — Partner Resource Center (external
    // partners only).  This release ships the shell + routing only;
    // full 8-section build lands in Phase 3 (v3.20-1502).
    // v3.21-1636 · Block 2 — VO Officer (internal compliance oversight
    // role) needs the Partner Resource Center in the sidebar even though
    // they are otherwise treated as internal.  Everyone else who is not
    // internal (external partners) also sees it.
    ...((!isInternal() || isVoOfficer()) ? [{ key: 'partner-resource', label: 'Partner Resource Center', icon: Building2, items: [
      { to: '/partner-resource-center', icon: Building2, label: 'Partner Resource Center' },
    ]}] : []),
    { key: 'communications', label: 'Communications', icon: MessageSquare, items: [
      { to: '/communications', icon: MessageSquare, label: 'Inbox',
        badge: unreadCommunications, flash: communicationsFlashing },
      { to: '/contact-directory', icon: UsersRound, label: 'Contact Directory' },
      // v3.18-1377 GMAIL-002 Phase 1 — Google Mailboxes management surface.
      // v3.19-1422 COMMS-MODERN — Google Mailboxes / Google Chat is now
      // available to every Hub user (internal + external) since every
      // user may connect their own Google account.  Each connection is
      // fully isolated to the caller.
      { to: '/google-mailboxes', icon: Mail, label: 'Google Mailboxes' },
      // v3.18-1379 PUSH-001 — per-user push notification preferences.
      { to: '/notification-preferences', icon: MessageSquare, label: 'Notification Preferences' },
      // v3.21-1707 — Phone System entry restricted to phone_system_admin
      // (ptc_admin / ptc_director).  Regular internal users get
      // /smart-dialer under People instead.
      ...(isInternal() && ['ptc_admin','ptc_director'].includes(user?.role)
          ? [{ to: '/phone-system', icon: PhoneCall, label: 'Phone System' }] : []),
      ...(isAdmin() ? [{ to: '/admin/email-templates',         icon: Mail, label: 'Email Templates' }] : []),
      ...(canAccessTemplateLibrary() ? [{ to: '/admin/template-library', icon: Mail, label: 'Template Library' }] : []),
      ...(isAdmin() ? [{ to: '/admin/partner-content',         icon: FileText, label: 'Partner Content Manager' }] : []),
      // v3.21-1815 — Ambiguous Intakes moved to PEOPLE (see line ~1180).
      ...(isAdmin() ? [{ to: '/notifications',                 icon: Bell, label: 'Notifications' }] : []),
      ...(isAdmin() ? [{ to: '/settings/appointment-reminders',icon: Bell, label: 'Reminder Settings' }] : []),
    ]},
    { key: 'finance', label: 'Finance', icon: DollarSign, items: [
      ...(canAccessPaymentCenter() ? [{ to: '/payment-center', icon: DollarSign, label: 'Payment Center' }] : []),
      ...(isAdmin() ? [{ to: '/revenue-dashboard',       icon: TrendingUp, label: 'Revenue Dashboard' }] : []),
      ...(isAdmin() ? [{ to: '/ptc-finance',             icon: TrendingUp, label: 'PTC Finance' }] : []),
      // v3.18-1316 — Money Manager sidebar restored.  Prior v3.18-1306
      // cleanup hid this behind ``REACT_APP_SHOW_MONEY_MANAGER`` which
      // was never enabled in Preview or Production, causing the CEO's
      // immutable financial ledger to disappear from navigation even
      // though the route, page component, backend router, and reader
      // adapter were all still live.  Restored to the pre-cleanup
      // behaviour: visible to any user the backend authorises (the API
      // 403s any caller other than the Money Manager owner).
      ...(isAdmin()
          ? [{ to: '/money-manager',           icon: DollarSign, label: 'Money Manager' }] : []),
      ...(isAdmin()
          ? [{ to: '/money-manager/lifecycle', icon: Filter,     label: 'Financial Lifecycle' }] : []),
    ]},
    { key: 'compensation', label: 'Compensation', icon: Wallet, items: [
      ...(isInternal() && user?.role !== 'ptc_principal_1' && companyFlags.compensation ? [{ to: '/my-money-manager',       icon: Wallet,      label: 'My Money Manager' }] : []),
      ...(isAdmin()    && companyFlags.compensation ? [{ to: '/master-compensation',   icon: Coins,       label: 'Master Money Manager' }] : []),
      // v3.18-1306 — My Earnings hidden behind admin flag until unique
      // operational purpose is defined vs My Money Manager.
      ...(isAdmin() && process.env.REACT_APP_SHOW_MY_EARNINGS === 'true'
          ? [{ to: '/earnings', icon: DollarSign, label: 'My Earnings' }] : []),
      // v3.18-1306 — Compensation Reports hidden until UUID entry fields
      // are replaced with searchable dropdowns (deferred feature build).
      ...(isAdmin() && companyFlags.compensation && process.env.REACT_APP_SHOW_COMPENSATION_REPORTS === 'true'
          ? [{ to: '/compensation-reports',  icon: FileBarChart,label: 'Compensation Reports' }] : []),
      ...(canSeeSettlementReport() && isInternal() ? [{ to: '/settlement', icon: DollarSign, label: 'Settlement' }] : []),
      ...(isAdmin()    && companyFlags.compensation ? [{ to: '/bonus-engine',          icon: Gift,        label: 'Bonus Engine' }] : []),
      ...(isAdmin()    && companyFlags.compensation ? [{ to: '/spiff-engine',          icon: TrendingUp,  label: 'Spiff Engine' }] : []),
      ...(isAdmin()    && companyFlags.compensation ? [{ to: '/chargeback-engine',     icon: Undo2,       label: 'Chargeback Engine' }] : []),
      ...(isAdmin()    && companyFlags.compensation ? [{ to: '/opening-balances',      icon: ScrollText,  label: 'Opening Balances' }] : []),
    ]},
    { key: 'payroll', label: 'Payroll', icon: Receipt, items: [
      ...(isAdmin() && companyFlags.payroll ? [{ to: '/payroll-engine', icon: Receipt,    label: 'Payroll Engine' }] : []),
      ...(isAdmin() ? [{ to: '/ptc-payouts',    icon: Shield,     label: 'Payout Approval' }] : []),
      ...(isAdmin() ? [{ to: '/partner-payroll',icon: DollarSign, label: 'Partner Payroll' }] : []),
    ]},
    { key: 'recognition', label: 'Recognition', icon: Trophy, items: [
      ...(isInternal() && companyFlags.career ? [{ to: '/career-dashboard', icon: Briefcase, label: 'My Career' }] : []),
      ...((companyFlags.recognition || companyFlags.leaderboards) && isAdmin() ? [{ to: '/recognition', icon: Trophy, label: 'Recognition & Leaderboards' }] : []),
      ...(isAdmin() && companyFlags.badges ? [{ to: '/badge-system', icon: Award, label: 'Badge System' }] : []),
    ]},
    { key: 'reports', label: 'Reports', icon: FileText, items: [
      { to: '/reports', icon: FileText, label: 'Reports' },
      ...((isAdmin() || ['ptc_director','ptc_manager','company_director','company_manager'].includes(user?.role))
        ? [{ to: '/admin/cf360-long-open', icon: ShieldAlert, label: 'CF360 Long-Open' }] : []),
    ]},
    { key: 'admin', label: 'Admin', icon: Shield, items: [
      ...(isAdmin() ? [{ to: '/admin/diagnostics',   icon: Wrench, label: 'Diagnostics' }] : []),
      ...(isAdmin() ? [{ to: '/admin/reconcile',     icon: Shield, label: 'Reconcile All' }] : []),
      ...(isAdmin() ? [{ to: '/data-integrity',      icon: Shield, label: 'Data Integrity' }] : []),
      ...(isAdmin() ? [{ to: '/data-import',         icon: Upload, label: 'Data Import' }] : []),
      ...(isAdmin() ? [{ to: '/ghost-appointments',  icon: Users,  label: 'Ghost Leads' }] : []),
    ]},
    { key: 'configuration', label: 'Configuration', icon: Settings, items: [
      ...(isAdmin() ? [{ to: '/company-settings',  icon: Settings,   label: 'Company Settings' }] : []),
      ...(isAdmin() && companyFlags.documents ? [{ to: '/company-documents', icon: FolderOpen, label: 'Company Documents' }] : []),
      ...(isAdmin() ? [{ to: '/field-templates',   icon: Settings,   label: 'Field Templates' }] : []),
      ...(inWorkspace ? [{ to: '/settings/availability', icon: Settings, label: 'Availability' }] : []),
    ]},
    { key: 'support-tail', label: null, items: [
      { to: '/support', icon: LifeBuoy, label: 'Support' },
    ]},
  ];

  // Prune groups whose visible-item list is empty (per-role RBAC may hide
  // an entire group). Prevents empty section headers from rendering.
  const visibleGroups = navGroups
    .map((g) => ({ ...g, items: (g.items || []).filter(Boolean) }))
    .filter((g) => g.items.length > 0);

  return (
    <ClientWorkspaceProvider>
    <TeamChatProvider>
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-primary transform ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 transition-transform duration-200 ease-in-out flex flex-col`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-primary-light/20">
          <div>
              <h1 className="font-heading text-xl font-bold text-white tracking-tight">
                PTC Client Hub
              </h1>
              <p className="text-xs text-white/60">Powered by Prestigious Companies</p>
            </div>
          <button
            className="lg:hidden text-white/80 hover:text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* v3.18-1274 HUB-ARCH-004 Slice A — grouped-sidebar renderer.
            Each group with a non-null label renders as a collapsible section;
            groups with label=null (marketing partners, support tail) render
            flat. Every leaf preserves its previous permission gate and
            deep-link URL. */}
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
          {visibleGroups.map((group, gi) => (
            <div
              key={group.key}
              className={gi === 0 ? '' : 'pt-2 mt-2 border-t border-primary-light/20'}
              data-testid={`nav-group-${group.key}`}
            >
              {group.label && (
                <button
                  data-testid={`nav-group-toggle-${group.key}`}
                  onClick={() => toggleGroup(group.key)}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-white/50 hover:text-white/80 hover:bg-primary-light/10 transition-all text-xs font-medium uppercase tracking-wider"
                >
                  {group.icon && <group.icon className="h-3.5 w-3.5" />}
                  <span className="flex-1 text-left">{group.label}</span>
                  {isGroupExpanded(group.key)
                    ? <ChevronUp className="h-3.5 w-3.5" />
                    : <ChevronDown className="h-3.5 w-3.5" />}
                </button>
              )}
              {(group.label === null || isGroupExpanded(group.key)) && (
                <div className={group.label ? 'mt-1 space-y-0.5' : 'space-y-0.5'}>
                  {group.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end || false}
                      data-testid={`nav-item-${item.to === '/' ? 'pipeline' : item.to.replace(/^\//,'').replace(/\//g,'-')}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-sm ${
                          isActive
                            ? 'bg-accent text-primary font-medium shadow-glow'
                            : 'text-white/70 hover:text-white hover:bg-primary-light/20'
                        } ${item.flash ? 'animate-pulse ring-2 ring-green-400 ring-offset-2 ring-offset-primary' : ''}`
                      }
                      onClick={() => {
                        setSidebarOpen(false);
                        if (item.to === '/communications') {
                          setUnreadCommunications(0);
                          setCommunicationsFlashing(false);
                        }
                        if (item.to === '/funded') {
                          // v3.21-1667 · per-user acknowledgment on nav.
                          setUnseenFundedCount(0);
                          setFundedFlashing(false);
                          try { api.post('/finturf-funded/acknowledge', {}).catch(() => {}); } catch (_) {}
                        }
                      }}
                    >
                      <item.icon className="h-4 w-4" />
                      <span className="flex-1">{item.label}</span>
                      {item.badge > 0 && (
                        <span
                          className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-semibold"
                          data-testid={`nav-badge-${item.to.replace(/^\//,'').replace(/\//g,'-')}`}
                        >
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* v3.21-1674 · CORRECTION — the legacy external-EQF quick-action
            has been REMOVED from the left sidebar.  The financing entry
            point is contextual to a specific client and lives inside the
            Client Info card of the Client Workspace as
            "Start Financing Application" (Client 1 & Client 2).  The
            sidebar has no financing shortcut. */}

        {/* Version Number */}
        <div className="px-4 py-2 mt-auto">
          <p className="text-xs text-white/40">{appVersion}</p>
        </div>

        {/* User Menu */}
        <div className="p-4 border-t border-primary-light/20">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white/70 hover:text-white hover:bg-primary-light/20 transition-colors">
                <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center text-primary font-semibold text-sm">
                  {user?.full_name?.charAt(0) || 'U'}
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
                  <p className="text-xs text-white/50 truncate">{user?.email}</p>
                </div>
                <ChevronDown className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate('/profile')}>
                <UserCircle className="h-4 w-4 mr-2" />
                Profile Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setChangePasswordOpen(true)}>
                <Key className="h-4 w-4 mr-2" />
                Change Password
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content — v3.21-1605: right margin only reserved for the
          Ghost dock when the user is INTERNAL.  External partners reclaim
          the full width. */}
      {/* v3.21-1622 · `min-w-0` allows the flex item to shrink below
          its intrinsic content width so wide tables (e.g. the Clients
          pipeline) can horizontally scroll INSIDE the main pane
          instead of pushing the entire page past the fixed right-side
          Ghost dock overlay. */}
      <main className={`flex-1 min-w-0 lg:ml-64 ${isInternalUser(user) ? 'xl:mr-64' : ''} flex flex-col min-h-screen`}>
        {/* Mobile Header */}
        <header className="lg:hidden sticky top-0 z-40 glass-header px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              className="p-2 rounded-lg hover:bg-slate-100"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="text-center">
              <h1 className="font-heading text-lg font-bold text-primary">PTC Client Hub</h1>
              <p className="text-xs text-slate-500">Powered by Prestigious Companies</p>
            </div>
            <div className="flex items-center gap-2">
              {canUseIntegratedPBX(user) && (
                <button
                  onClick={() => setDialPadOpen(true)}
                  className="p-2 rounded-lg hover:bg-slate-100"
                  data-testid="mobile-dial-pad-btn"
                >
                  <PhoneCall className="h-5 w-5 text-emerald-600" />
                </button>
              )}
              <Button 
                size="sm"
                onClick={() => setQuickAddClientOpen(true)}
                className="bg-accent hover:bg-gold-dark"
                data-testid="mobile-add-client-btn"
              >
                <Plus className="h-4 w-4" />
              </Button>
              <button
                onClick={() => navigate('/support')}
                className="p-2 rounded-lg hover:bg-slate-100"
                data-testid="mobile-support-btn"
              >
                <LifeBuoy className="h-5 w-5 text-blue-600" />
              </button>
            </div>
          </div>
        </header>

        {/* Desktop Header */}
        <header className="hidden lg:block sticky top-0 z-40 glass-header">
          <div className="flex items-center justify-between px-8 py-4">
            <div>
              <h2 className="font-heading text-xl font-semibold text-primary">
                Welcome back, {user?.full_name?.split(' ')[0]}
              </h2>
              <p className="text-sm text-slate-500">{user?.company_name}</p>
            </div>
            {/* v3.15-909: Header Phone + Extension. Show 757-448-3705 ONLY for the
                EasyQuickFund / PTC / Prestigious Travel Club Inc family. Hidden for
                every other company. Extension pulled from user.extension; the "Ext:"
                label is hidden when no extension is on file. */}
            {(() => {
              const HUB_FAMILY = new Set([
                'ptc',
                'prestigious travel club',
                'prestigious travel club inc',
                'prestigious travel club, inc',
                'prestigious travel club, inc.',
                'easyquickfund',
                'easy quick fund',
                'eqf',
              ]);
              const co = (user?.company_name || '').trim().toLowerCase();
              const showHubPhone = isPtcUser() || HUB_FAMILY.has(co);
              if (!showHubPhone) return null;
              const ext = (user?.extension || '').toString().trim();
              return (
                <div className="flex items-center gap-1.5 text-sm text-slate-600" data-testid="header-phone-display">
                  <Phone className="h-3.5 w-3.5 text-slate-400" />
                  <span className="font-medium">757-448-3705</span>
                  {ext && (
                    <span className="text-slate-400">| Ext: <span className="font-medium text-slate-600">{ext}</span></span>
                  )}
                </div>
              );
            })()}
            <div className="flex items-center gap-4">
              {/* v3.15-1094: real-time notification bell + SSE listener */}
              <NotificationCenter />
              {/* v3.17-1240 — persistent CRITICAL alert tray. */}
              <PersistentNotificationTray />
              {/* Rep Status Toggle - Only for PTC users */}
              {isPtcUser() && (
                <DropdownMenu open={statusDropdownOpen} onOpenChange={setStatusDropdownOpen}>
                  <DropdownMenuTrigger asChild>
                    <button 
                      className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
                      data-testid="rep-status-toggle"
                      disabled={statusLoading}
                    >
                      {statusLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Circle 
                            className={`h-3 w-3 ${
                              repStatus === 'available' ? 'fill-green-500 text-green-500' :
                              repStatus === 'paused' ? 'fill-yellow-500 text-yellow-500' :
                              'fill-slate-400 text-slate-400'
                            }`} 
                          />
                          <span className="text-sm font-medium text-slate-700 capitalize">{repStatus}</span>
                          <ChevronDown className="h-4 w-4 text-slate-400" />
                        </>
                      )}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <div className="px-2 py-1.5 text-xs text-slate-500 font-medium">
                      Set Availability Status
                    </div>
                    <DropdownMenuSeparator />
                    {REP_STATUS_OPTIONS.map((option) => (
                      <DropdownMenuItem 
                        key={option.value}
                        onClick={() => updateRepStatus(option.value)}
                        className="flex items-center gap-2 cursor-pointer"
                        data-testid={`rep-status-${option.value.toLowerCase()}`}
                      >
                        <Circle className={`h-3 w-3 ${option.color} ${option.value === repStatus ? 'fill-current' : ''}`} />
                        <span>{option.label}</span>
                        {option.value === repStatus && (
                          <span className="ml-auto text-xs text-slate-500">Current</span>
                        )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              
              {/* Global Quick Add Client Button */}
              <Button 
                onClick={() => setQuickAddClientOpen(true)}
                className="bg-accent hover:bg-gold-dark"
                data-testid="global-add-client-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Client / Deal
              </Button>
              
              {/* v3.15-579: Global Dial Pad Button
                  v3.18-1294: gated to authorized phone-system companies. */}
              {canUseIntegratedPBX(user) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDialPadOpen(true)}
                  className="h-9 gap-1.5 border-slate-200 hover:bg-slate-50"
                  data-testid="global-dial-pad-btn"
                >
                  <PhoneCall className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm">Dial</span>
                </Button>
              )}

              {/* v3.21-1618 · Global Call Workspace launcher — always
                  accessible from the header.  Dispatches a window
                  event that ActiveCallPill listens for; it opens the
                  IDLE state when no call is active, or reopens the
                  ACTIVE / WRAP-UP call otherwise. */}
              {canUseIntegratedPBX(user) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('open-call-workspace'));
                  }}
                  className="h-9 gap-1.5 border-slate-200 hover:bg-slate-50"
                  data-testid="global-call-workspace-btn"
                  title="Open Call Workspace (idle · active · wrap-up)"
                >
                  <Phone className="h-4 w-4 text-slate-700" />
                  <span className="text-sm">Call Workspace</span>
                </Button>
              )}

              {/* v3.21-1735 · SMS Dashboard replaces the permanent Transfer
                  Call button.  Actual call transfer remains available inside
                  the ActiveCallPill / Call Workspace surfaces.  Badge shows
                  Deal-Ownership-scoped unread + Needs Review + Unknown
                  Sender counts (backend enforces visibility). */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/sms-dashboard')}
                className="h-9 gap-1.5 border-slate-200 hover:bg-slate-50 relative"
                data-testid="global-sms-dashboard-btn"
                title="Open SMS Dashboard"
              >
                <MessageSquare className="h-4 w-4 text-blue-600" />
                <span className="text-sm">SMS Dashboard</span>
                {smsBadgeTotal > 0 && (
                  <span
                    data-testid="global-sms-dashboard-badge"
                    className="inline-flex text-[10px] font-bold text-white bg-rose-600 rounded-full h-4 min-w-4 px-1 items-center justify-center"
                  >
                    {smsBadgeTotal > 99 ? '99+' : smsBadgeTotal}
                  </span>
                )}
              </Button>
              
              {/* v3.15-581: Support button — visible to ALL users */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/support')}
                className="h-9 gap-1.5 border-slate-200 hover:bg-slate-50"
                data-testid="global-support-btn"
              >
                <LifeBuoy className="h-4 w-4 text-blue-600" />
                <span className="text-sm">Support</span>
              </Button>

              {/* v3.14-172: View As User — PTC Admin only */}
              {isRealAdmin() && (
                <DropdownMenu onOpenChange={(open) => { if (open) fetchViewAsUsers(); }}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" data-testid="view-as-user-btn">
                      <Eye className="h-3.5 w-3.5" />
                      View As
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72 max-h-80 overflow-y-auto">
                    <div className="px-3 py-2 border-b">
                      <p className="text-sm font-semibold">View As User</p>
                      <p className="text-[10px] text-slate-500">See exactly what they see</p>
                    </div>
                    {viewAsLoading ? (
                      <div className="p-4 text-center"><Loader2 className="h-4 w-4 animate-spin mx-auto text-slate-400" /></div>
                    ) : viewAsUsers.length === 0 ? (
                      <div className="p-4 text-center text-xs text-slate-500">No other users</div>
                    ) : (
                      viewAsUsers.map(u => (
                        <DropdownMenuItem
                          key={u.id}
                          className="flex flex-col items-start py-2 cursor-pointer"
                          onClick={() => setViewAsUser(u)}
                          data-testid={`view-as-${u.id}`}
                        >
                          <span className="font-medium text-sm">{u.full_name || u.email}</span>
                          <span className="text-[10px] text-slate-500">{u.role?.replace(/_/g, ' ')} {u.company_name ? `· ${u.company_name}` : ''}</span>
                        </DropdownMenuItem>
                      ))
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Settings Button - For PTC managers only */}
              {isPtcUser() && ['ptc_admin', 'ptc_manager', 'ptc_director'].includes(user?.role) && (
                <Popover open={settingsOpen} onOpenChange={setSettingsOpen}>
                  <PopoverTrigger asChild>
                    <button 
                      className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
                      data-testid="settings-btn"
                    >
                      <Settings className="h-5 w-5 text-slate-600" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-72">
                    <div className="space-y-4">
                      <div className="font-medium text-slate-800 flex items-center gap-2">
                        <Settings className="h-4 w-4" />
                        Notification Settings
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                            <Mail className="h-4 w-4 text-slate-500" />
                            Daily Manager Digest
                          </label>
                          <p className="text-xs text-slate-500">
                            Receive daily email summary of application performance
                          </p>
                        </div>
                        <Switch
                          checked={digestEnabled}
                          onCheckedChange={toggleDigest}
                          disabled={digestLoading}
                          data-testid="digest-toggle"
                        />
                      </div>
                      {isInternal() && (
                      <div className="flex items-center justify-between border-t pt-3">
                        <div className="space-y-0.5">
                          <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                            <Bell className="h-4 w-4 text-slate-500" />
                            ACH Sound Alert
                          </label>
                          <p className="text-xs text-slate-500">
                            Play sound when banking info arrives
                          </p>
                        </div>
                        <Switch
                          checked={achSoundOn}
                          onCheckedChange={(v) => { setAchSoundOn(v); setSoundEnabled(v); }}
                          data-testid="ach-sound-toggle"
                        />
                      </div>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
              
              <span className="text-sm px-3 py-1 rounded-full bg-accent/10 text-accent font-medium capitalize">
                {user?.role?.replace('_', ' ')}
              </span>
            </div>
          </div>
        </header>

        {/* v3.14-211: View As User Banner — STICKY */}
        {isViewingAsUser && (
          <div className="sticky top-0 z-50 bg-amber-500 text-white px-4 py-2 flex items-center justify-between text-sm shadow-md" data-testid="view-as-banner">
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              <span>Viewing as: <strong>{user?.full_name || user?.email}</strong> ({user?.role?.replace(/_/g, ' ')})</span>
            </div>
            <Button size="sm" variant="secondary" className="h-7 text-xs bg-white text-amber-700 hover:bg-amber-50 font-semibold" onClick={exitViewAs} data-testid="exit-view-as-btn">
              Exit View As
            </Button>
          </div>
        )}

        {/* Page Content — v3.16-1236 density pass: tightened main padding to reclaim horizontal space between the 256px left nav and 256px right GDO dock. */}
        <div className="flex-1 px-4 py-4 lg:px-6 lg:py-5">
          <div className="max-w-screen-2xl mx-auto">
            {/* v3.18-1303 — App-shell error boundary.  If a route's
                page or any modal it opens throws during render, the
                sidebar, top nav, active-call pill, and Ghost console
                stay operational and the operator sees a recovery
                card.  Operator work in Compose & Approve / Call
                Outcome autosaves to localStorage on every keystroke
                (see the respective components). */}
            <AppErrorBoundary>
              <RequiredDocsBanner />
              <AnnouncementsBanner />
              <Outlet />
            </AppErrorBoundary>
            {/* v3.15-916: Task-due popups moved to Ghost (GhostFloatingIcon).
                Standalone TaskDueNotifier removed — Ghost is the only source. */}
          </div>
        </div>
        
        {/* Global Footer */}
        <Footer />
      </main>

      {/* v3.18-1294 — Every phone-system component is wrapped in a
          SectionErrorBoundary so a Twilio SDK crash / provider
          exception / WebRTC failure NEVER blanks the page.  The active
          client record and unsaved operator work always survive.
          Access-control gate: only Prestigious Travel Club, PTC
          Foundation, and EasyQuickFund users see any phone UI. */}
      {canUseIntegratedPBX(user) && (
        <SectionErrorBoundary label="Dial Pad" testId="dashboard-dial-pad-error">
          <DialPadModal
            isOpen={dialPadOpen}
            onClose={() => { setDialPadOpen(false); setDialPadInitial(''); }}
            initialNumber={dialPadInitial}
          />
        </SectionErrorBoundary>
      )}

      {/* v3.15-1131: Global Active Call Pill — floats bottom-right while a
          call is active, follows the user across pages. */}
      {canUseIntegratedPBX(user) && (
        <SectionErrorBoundary label="Active Call" testId="dashboard-active-call-error">
          <ActiveCallPill />
        </SectionErrorBoundary>
      )}

      {/* v3.21-1641 · Global Client-Context Compose Listener — mounts
          the shared ComposeAndApprove composer and opens it whenever a
          "Send Email" click from a known-client surface dispatches
          `hub:open-compose`.  Auto-populates the To field with the
          client's authoritative primary email. */}
      <SectionErrorBoundary label="Client Compose" testId="dashboard-client-compose-error">
        <ClientContextComposeListener />
      </SectionErrorBoundary>

      {/* v3.15-1150: Global Pending-Call-Documentation watcher — polls every
          30s and mounts a non-dismissible modal if the rep has any unfilled
          inbound-call documentation. */}
      {canUseIntegratedPBX(user) && (
        <SectionErrorBoundary label="Pending Disposition" testId="dashboard-pending-disp-error">
          <PendingDispositionWatcher enabled />
        </SectionErrorBoundary>
      )}

      {/* v3.21-1735 · TransferCallModal is retained here as an inert
          sibling so any code path that still expects the top-nav mount
          point continues to compile.  It is never opened from the
          global top-nav (that button is now SMS Dashboard).  Active
          call transfer remains available inside ActiveCallPill / Call
          Workspace via the same TransferCallModal instance mounted
          there. */}
      <TransferCallModal
        isOpen={transferModalOpen}
        onClose={() => setTransferModalOpen(false)}
        activeCall={topNavActiveCall}
        onTransferred={() => { /* pill will repoll on its own */ }}
      />

      {/* Smart Client Intake Dialog */}
      <Dialog open={quickAddClientOpen} onOpenChange={setQuickAddClientOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Plus className="h-5 w-5 text-accent" />
              Smart Client Intake
            </DialogTitle>
          </DialogHeader>
          
          {/* Duplicate Warning */}
          {duplicateWarning && duplicateWarning.length > 0 && (
            <Card className="border-amber-300 bg-amber-50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-amber-800">Possible existing client found</p>
                    <p className="text-sm text-amber-700 mb-3">
                      A client with matching email or phone already exists.
                    </p>
                    {duplicateWarning.map((dup) => (
                      <div key={dup.id} className="flex items-center justify-between bg-white rounded-lg p-3 mb-2 border border-amber-200">
                        <div>
                          <p className="font-medium text-slate-800">{dup.full_name}</p>
                          <p className="text-sm text-slate-500">{dup.email} • {dup.phone || 'No phone'}</p>
                          <Badge variant="secondary" className="mt-1 text-xs">
                            {(dup.deal_stage || 'new_lead').replace(/_/g, ' ')}
                          </Badge>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUseExistingClient(dup)}
                          className="border-accent text-accent hover:bg-accent hover:text-white"
                          data-testid={`use-existing-${dup.id}`}
                        >
                          <User className="h-4 w-4 mr-1" />
                          Use Existing
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          <form onSubmit={handleQuickAddClient} className="space-y-6">
            {/* Section 1: Client Information */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">
                Client Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name *</Label>
                  <Input
                    value={quickAddData.first_name}
                    onChange={(e) => setQuickAddData({ ...quickAddData, first_name: e.target.value })}
                    required
                    placeholder="John"
                    data-testid="quick-add-first-name"
                  />
                </div>
                <div>
                  <Label>Last Name *</Label>
                  <Input
                    value={quickAddData.last_name}
                    onChange={(e) => setQuickAddData({ ...quickAddData, last_name: e.target.value })}
                    required
                    placeholder="Smith"
                    data-testid="quick-add-last-name"
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <div className="relative">
                    <Input
                      type="email"
                      value={quickAddData.email}
                      onChange={(e) => setQuickAddData({ ...quickAddData, email: e.target.value })}
                      required
                      placeholder="john@example.com"
                      data-testid="quick-add-email"
                    />
                    {checkingDuplicate && (
                      <Loader2 className="h-4 w-4 animate-spin absolute right-3 top-3 text-slate-400" />
                    )}
                  </div>
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    type="tel"
                    value={quickAddData.phone}
                    onChange={(e) => setQuickAddData({ ...quickAddData, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    data-testid="quick-add-phone"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Street Address</Label>
                  <Input
                    value={quickAddData.street_address}
                    onChange={(e) => setQuickAddData({ ...quickAddData, street_address: e.target.value })}
                    placeholder="123 Main St"
                  />
                </div>
                <div>
                  <Label>City</Label>
                  <Input
                    value={quickAddData.city}
                    onChange={(e) => setQuickAddData({ ...quickAddData, city: e.target.value })}
                    placeholder="New York"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>State</Label>
                    <Input
                      value={quickAddData.state}
                      onChange={(e) => setQuickAddData({ ...quickAddData, state: e.target.value })}
                      placeholder="NY"
                      maxLength={2}
                    />
                  </div>
                  <div>
                    <Label>Zip</Label>
                    <Input
                      value={quickAddData.zip_code}
                      onChange={(e) => setQuickAddData({ ...quickAddData, zip_code: e.target.value })}
                      placeholder="10001"
                    />
                  </div>
                </div>
                {isInternal() && (
                  <div className="col-span-2">
                    <Label>Partner Company *</Label>
                    <Select 
                      value={quickAddData.company_id} 
                      onValueChange={(value) => setQuickAddData({ ...quickAddData, company_id: value })}
                    >
                      <SelectTrigger data-testid="quick-add-company">
                        <SelectValue placeholder="Select partner company" />
                      </SelectTrigger>
                      <SelectContent>
                        {companies.map((company) => (
                          <SelectItem key={company.id} value={company.id}>
                            {company.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>

            {/* Section 2: Credit Application Details */}
            <div className="border-t pt-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">
                Credit Application Details
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    value={quickAddData.date_of_birth}
                    onChange={(e) => setQuickAddData({ ...quickAddData, date_of_birth: e.target.value })}
                    data-testid="quick-add-dob"
                  />
                </div>
                <div>
                  <Label>SSN (Full 9 digits)</Label>
                  <Input
                    value={quickAddData.ssn}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 9);
                      setQuickAddData({ ...quickAddData, ssn: val });
                    }}
                    placeholder="123456789"
                    maxLength={9}
                    data-testid="quick-add-ssn"
                  />
                  <p className="text-xs text-slate-500 mt-1">Required for credit processing</p>
                </div>
                <div>
                  <Label>Employer Name</Label>
                  <Input
                    value={quickAddData.employer_name}
                    onChange={(e) => setQuickAddData({ ...quickAddData, employer_name: e.target.value })}
                    placeholder="ABC Corporation"
                    data-testid="quick-add-employer"
                  />
                </div>
                <div>
                  <Label>Job Title</Label>
                  <Input
                    value={quickAddData.job_title}
                    onChange={(e) => setQuickAddData({ ...quickAddData, job_title: e.target.value })}
                    placeholder="Manager"
                    data-testid="quick-add-job-title"
                  />
                </div>
                <div>
                  <Label>Monthly Income</Label>
                  <Input
                    type="number"
                    value={quickAddData.monthly_income}
                    onChange={(e) => setQuickAddData({ ...quickAddData, monthly_income: e.target.value })}
                    placeholder="5000"
                    data-testid="quick-add-income"
                  />
                </div>
                <div>
                  <Label>Employment Length</Label>
                  <Select 
                    value={quickAddData.employment_length || "none"} 
                    onValueChange={(value) => setQuickAddData({ ...quickAddData, employment_length: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="quick-add-employment-length">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select</SelectItem>
                      <SelectItem value="less_than_1_year">Less than 1 year</SelectItem>
                      <SelectItem value="1_3_years">1-3 years</SelectItem>
                      <SelectItem value="3_5_years">3-5 years</SelectItem>
                      <SelectItem value="5_plus_years">5+ years</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Residence Type</Label>
                  <Select 
                    value={quickAddData.residence_type || "none"} 
                    onValueChange={(value) => setQuickAddData({ ...quickAddData, residence_type: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="quick-add-residence-type">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select</SelectItem>
                      <SelectItem value="own">Own</SelectItem>
                      <SelectItem value="rent">Rent</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Bank Name</Label>
                  <Input
                    value={quickAddData.bank_name}
                    onChange={(e) => setQuickAddData({ ...quickAddData, bank_name: e.target.value })}
                    placeholder="Chase"
                    data-testid="quick-add-bank"
                  />
                </div>
                <div>
                  <Label>Account Type</Label>
                  <Select 
                    value={quickAddData.account_type || "none"} 
                    onValueChange={(value) => setQuickAddData({ ...quickAddData, account_type: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="quick-add-account-type">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select</SelectItem>
                      <SelectItem value="checking">Checking</SelectItem>
                      <SelectItem value="savings">Savings</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Loan Amount Requested</Label>
                  <Input
                    type="number"
                    value={quickAddData.loan_amount_requested}
                    onChange={(e) => setQuickAddData({ ...quickAddData, loan_amount_requested: e.target.value })}
                    placeholder="25000"
                    data-testid="quick-add-loan-amount"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Loan Purpose</Label>
                  <Input
                    value={quickAddData.loan_purpose}
                    onChange={(e) => setQuickAddData({ ...quickAddData, loan_purpose: e.target.value })}
                    placeholder="Debt consolidation, home improvement, etc."
                    data-testid="quick-add-loan-purpose"
                  />
                </div>
                <div>
                  <Label>Originating Rep</Label>
                  <Input
                    value={quickAddData.originating_rep}
                    onChange={(e) => setQuickAddData({ ...quickAddData, originating_rep: e.target.value })}
                    placeholder="John Smith"
                    data-testid="quick-add-orig-rep"
                  />
                </div>
                <div>
                  <Label>Originating Rep Email</Label>
                  <Input
                    type="email"
                    value={quickAddData.originating_rep_email}
                    onChange={(e) => setQuickAddData({ ...quickAddData, originating_rep_email: e.target.value })}
                    placeholder="john@company.com"
                    data-testid="quick-add-orig-rep-email"
                  />
                </div>
              </div>
            </div>

            {/* Section 3: Deal Information */}
            <div className="border-t pt-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wide">
                Deal Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Sales Rep</Label>
                  <Input
                    value={quickAddData.sales_rep_name}
                    onChange={(e) => setQuickAddData({ ...quickAddData, sales_rep_name: e.target.value })}
                    placeholder="Sales rep name"
                  />
                </div>
                {isInternal() && (
                  <div>
                    <Label>PTC Rep</Label>
                    <Select 
                      value={quickAddData.ptc_rep_id || "none"} 
                      onValueChange={(value) => setQuickAddData({ ...quickAddData, ptc_rep_id: value === "none" ? "" : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select PTC rep" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {ptcReps.map((rep) => (
                          <SelectItem key={rep.id} value={rep.id}>
                            {rep.full_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>Deal Amount</Label>
                  <Input
                    type="number"
                    value={quickAddData.deal_amount}
                    onChange={(e) => setQuickAddData({ ...quickAddData, deal_amount: e.target.value })}
                    placeholder="25000"
                  />
                </div>
                <div>
                  <Label>Approved Amount</Label>
                  <Input
                    type="number"
                    value={quickAddData.approved_amount}
                    onChange={(e) => setQuickAddData({ ...quickAddData, approved_amount: e.target.value })}
                    placeholder="20000"
                  />
                </div>
                {isInternal() && (
                  <div>
                    <Label>Lending Institution</Label>
                    <Select 
                      value={quickAddData.lending_institution || "none"} 
                      onValueChange={(value) => setQuickAddData({ ...quickAddData, lending_institution: value === "none" ? "" : value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select lender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {LENDERS.map((lender) => (
                          <SelectItem key={lender} value={lender}>
                            {lender}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>Deal Stage</Label>
                  <Select 
                    value={quickAddData.deal_stage} 
                    onValueChange={(value) => setQuickAddData({ ...quickAddData, deal_stage: value })}
                  >
                    <SelectTrigger data-testid="quick-add-stage">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DEAL_STAGES.map((stage) => (
                        <SelectItem key={stage.value} value={stage.value}>
                          {stage.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>ACH ETA</Label>
                  <Input
                    type="date"
                    value={quickAddData.ach_eta}
                    onChange={(e) => setQuickAddData({ ...quickAddData, ach_eta: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={quickAddData.notes}
                    onChange={(e) => setQuickAddData({ ...quickAddData, notes: e.target.value })}
                    placeholder="Any initial notes about this client/deal..."
                    rows={2}
                  />
                </div>
              </div>
            </div>

            {/* v3.15-1230 SMART-INTAKE-003 — Payment Methods (Standard / CC / ACH) */}
            <div className="border-t pt-6">
              <ClientPaymentMethodsIntake
                mode={intakeMode}
                onModeChange={setIntakeMode}
                creditCards={intakeCreditCards}
                onCreditCardsChange={setIntakeCreditCards}
                achAccounts={intakeAchAccounts}
                onAchAccountsChange={setIntakeAchAccounts}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setQuickAddClientOpen(false);
                  setDuplicateWarning(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-accent hover:bg-gold-dark"
                disabled={quickAddLoading}
                data-testid="quick-add-submit"
              >
                {quickAddLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Save Client & Deal
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-primary" />
              Change Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {passwordError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                {passwordError}
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Current Password</Label>
              <Input
                id="currentPassword"
                type="password"
                placeholder="Enter current password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                data-testid="current-password-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                placeholder="Enter new password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                data-testid="new-password-input"
              />
              <p className="text-xs text-slate-500">Must be at least 8 characters</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm new password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                data-testid="confirm-password-input"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => {
              setChangePasswordOpen(false);
              setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
              setPasswordError('');
            }}>
              Cancel
            </Button>
            <Button onClick={handleChangePassword} disabled={passwordLoading}>
              {passwordLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Changing...
                </>
              ) : (
                'Change Password'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* v3.21-1605 · Ghost surfaces are INTERNAL-ONLY. External partners
          get zero Ghost DOM, zero polling, zero websocket, zero traffic. */}
      {isInternalUser(user) && <GhostFloatingIcon />}

      {/* v3.15-1232 Ghost Daily Organizer — permanent right-side dock (internal only). */}
      {user && isInternalUser(user) && <GhostDailyOrganizer currentUser={user} />}
      {user && <ApplicationAssignmentGate currentUser={user} />}

      {/* Force password change modal */}
      {mustChangePassword && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center" data-testid="force-pw-modal">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm space-y-4">
            <h2 className="text-lg font-bold text-slate-800">Update Your Password</h2>
            <p className="text-sm text-slate-500">Your account requires a password update before you can continue.</p>
            <div>
              <label className="text-xs text-slate-500 block mb-1">New Password</label>
              <input type="password" className="w-full border rounded px-3 py-2 text-sm" value={newPw} onChange={e => { setNewPw(e.target.value); setPwError(''); }} placeholder="Enter new password" data-testid="force-pw-new" />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Confirm Password</label>
              <input type="password" className="w-full border rounded px-3 py-2 text-sm" value={confirmPw} onChange={e => { setConfirmPw(e.target.value); setPwError(''); }} placeholder="Confirm new password" data-testid="force-pw-confirm" />
            </div>
            {pwError && <p className="text-xs text-red-600">{pwError}</p>}
            <button
              className="w-full bg-blue-600 text-white rounded py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              disabled={pwChanging || !newPw || !confirmPw}
              data-testid="force-pw-submit"
              onClick={async () => {
                if (newPw.length < 6) { setPwError('Password must be at least 6 characters'); return; }
                if (newPw !== confirmPw) { setPwError('Passwords do not match'); return; }
                setPwChanging(true);
                try {
                  await api.post('/auth/force-change-password', { new_password: newPw });
                  clearMustChangePassword();
                  setNewPw('');
                  setConfirmPw('');
                } catch (err) {
                  setPwError(err?.response?.data?.detail || 'Failed to change password');
                } finally { setPwChanging(false); }
              }}
            >
              {pwChanging ? 'Updating...' : 'Update Password'}
            </button>
          </div>
        </div>
      )}
      {/* v3.21-1620 · Global Client Workspace — single reusable
          instance mounted here so no upstream remount can destroy it.
          Only the explicit Close button in the modal closes it. */}
      <ClientWorkspaceModal />
      {/* v3.21-1621 · Global Team Chat — internal employee chat with
          client context.  X closes the window WITHOUT destroying the
          server-side thread; reopening resumes the same conversation. */}
      <TeamChatWindow />
      {/* v3.21-1624 · Global Unknown Inbound Caller pane — auto-opens
          from ANY Hub page when an inbound call arrives without a
          resolved client.  Never creates a fake client.  Provides a
          Find/Link Client search that hands off to the standard
          ClientWorkspaceModal once the caller is identified. */}
      <UnknownCallerModal />
    </div>
    </TeamChatProvider>
    </ClientWorkspaceProvider>
  );
};

export default DashboardLayout;
